<script lang="ts">
    import type {UUID, ViewableFile} from 'typings/portaro.be.types';
    import type {TouchSwipeEvent} from '../../actions/use.touch-swipe';
    import {touchSwipe} from '../../actions/use.touch-swipe';
    import {getMediaViewerContext} from '../../lib/mv-context';
    import {onDestroy, onMount, tick} from 'svelte';
    import {createViewedContentContext} from '../../lib/mv-viewed-content';
    import {fly} from 'svelte/transition';
    import {exists, uuid} from 'shared/utils/custom-utils';
    import {FileViewForm} from 'shared/constants/portaro.constants';
    import {pipe} from 'core/utils';
    import {byteFormatter} from 'shared/utils/pipes';
    import {viewableFileContainsViewForm} from '../../lib/mv-utils';
    import {pinchZoom} from '../../actions/use.pinch-zoom';
    import {isCurrentLayoutOrSmaller, MediaViewerLayoutSize, mediaViewerLayoutWidth} from '../../lib/mv-layout';
    import {get} from 'svelte/store';
    import {getLocalization} from 'core/svelte-context/context';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import MvTopBar from './mv-topbar/MvTopBar.svelte';
    import MvFloatingControls from './mv-floating-controls/MvFloatingControls.svelte';
    import MvImageFile from './mv-files-types/MvImageFile.svelte';
    import MvDirectoryOrFileIcon from '../mv-misc/MvDirectoryOrFileIcon.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import MvTextFile from './mv-files-types/MvTextFile.svelte';
    import MvVideoFile from './mv-files-types/MvVideoFile.svelte';
    import MvAudioFile from './mv-files-types/MvAudioFile.svelte';
    import MvPdfFile from './mv-files-types/MvPdfFile.svelte';
    import {mediaViewerSidebarOpen, toggleMediaViewerSidebar} from '../../lib/mv-sidebar';
    import {NEXT_IMAGE_EVENT, PREVIOUS_IMAGE_EVENT} from '../../lib/mv-constants';

    const context = getMediaViewerContext();
    const viewedContent = createViewedContentContext();
    const flyInAnimParams = {y: 15, duration: 375};
    const supportedViewForms = [FileViewForm.IMAGE, FileViewForm.RICH_DOCUMENT, FileViewForm.PLAIN_TEXT, FileViewForm.VIDEO, FileViewForm.AUDIO, FileViewForm.PDF];
    const localize = getLocalization();

    let viewedFile: ViewableFile | null;
    let viewedFileChangeReactivityTriggerKey: UUID;
    let presentationMode = false;
    let canSwipeNavigate = false;
    let pinchZooming = false;
    let imageSwitchAnimationDirection = 0;
    const presentationModeUnsubscribe = viewedContent.presentationMode.subscribe((currentPresentationMode) => presentationMode = currentPresentationMode);
    const canSwipeNavigateUnsubscribe = viewedContent.canSwipeNavigate.subscribe((currentCanSwipeNavigate) => canSwipeNavigate = currentCanSwipeNavigate);
    const viewedFileUnsubscribe = context.viewedFile.subscribe((currentViewedFile) => {
        viewedFileChangeReactivityTriggerKey = uuid();
        viewedFile = currentViewedFile;
        viewedContent.resetToDefaults();

        if (context.viewerType === 'media-viewer') {
            context.urlManagementService.resetToDefault();
        }

        setTimeout(() => {
            imageSwitchAnimationDirection = 0;
        }, 100);
    });

    let viewportWidth = 0;
    let viewportHeight = 0;
    $: viewedContent.setViewportSize(viewportWidth, viewportHeight);
    $: mobileSidebarOpen = isCurrentLayoutOrSmaller(MediaViewerLayoutSize.TABLET, $mediaViewerLayoutWidth) && $mediaViewerSidebarOpen;

    onMount(() => {
        window.addEventListener('keydown', handleKeyDown);
    });

    onDestroy(() => {
        window.removeEventListener('keydown', handleKeyDown);

        viewedFileUnsubscribe();
        presentationModeUnsubscribe();
        canSwipeNavigateUnsubscribe();
    });

    function isSupportedViewForm(file: ViewableFile): boolean {
        const supported = file.viewForms.filter((form) => supportedViewForms.includes(form));
        return supported.length > 0;
    }

    const handleTouchSwipe = (event: TouchSwipeEvent) => {
        if (!canSwipeNavigate || pinchZooming) {
            return;
        }

        if (event.detail === 'right' && !$mediaViewerSidebarOpen) {
            toggleMediaViewerSidebar(true);
            return;
        }

        if (event.detail === 'up') {
            imageSwitchAnimationDirection = -1;
            viewedContent.eventBus.dispatchEvent(new CustomEvent(PREVIOUS_IMAGE_EVENT));
        }

        if (event.detail === 'down') {
            imageSwitchAnimationDirection = 1;
            viewedContent.eventBus.dispatchEvent(new CustomEvent(NEXT_IMAGE_EVENT));
        }
    }

    let startingZoomValue = 0;

    const handlePinchZoom = (event: CustomEvent<number>) => {
        viewedContent.setZoom(startingZoomValue * event.detail);
    }

    const handlePinchZoomStart = () => {
        startingZoomValue = get(viewedContent.zoom);
        pinchZooming = true;
    }

    const handlePinchZoomEnd = () => {
        tick().then(() => pinchZooming = false);
    }

    const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') || !viewableFileContainsViewForm(viewedFile, FileViewForm.IMAGE, FileViewForm.THUMBNAIL)) {
            return;
        }

        if (event.key === 'ArrowLeft') {
            viewedContent.eventBus.dispatchEvent(new CustomEvent(PREVIOUS_IMAGE_EVENT));
        }

        if (event.key === 'ArrowRight') {
            viewedContent.eventBus.dispatchEvent(new CustomEvent(NEXT_IMAGE_EVENT));
        }
    }

    const handleFileDownloadClick = async (file: ViewableFile) => {
        await context.service.downloadFile(file);
    }
</script>

{#if context.viewerType === 'media-viewer'}
    <MvTopBar fileAccessAllowed="{exists(viewedFile) && viewedFile.showPermission.allowed}"/>
{/if}

{#key viewedFileChangeReactivityTriggerKey}
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="mv-viewed-content"
         use:touchSwipe={{disabled: mobileSidebarOpen}}
         use:pinchZoom={{disabled: mobileSidebarOpen}}
         on:touch-swipe={handleTouchSwipe}
         on:pinch-zoom={handlePinchZoom}
         on:pinch-zoom-start={handlePinchZoomStart}
         on:pinch-zoom-end={handlePinchZoomEnd}
         in:fly={flyInAnimParams}
         bind:clientWidth={viewportWidth}
         bind:clientHeight={viewportHeight}>

        {#if !exists(viewedFile)}
            <div class="info-container">
                <IconedContent orientation="vertical"
                               align="center"
                               icon="info"
                               textAlign="center"
                               iconColor="var(--brand-orange-new)">

                    {localize(/* @kp-localization mediaviewer.content.NoFileSelectedLabel */ 'mediaviewer.content.NoFileSelectedLabel')}
                </IconedContent>
            </div>
        {:else}
            {#if isSupportedViewForm(viewedFile)}
                {#if !viewedFile.showPermission.allowed}
                    <div class="unsupported-file-container no-view-permission">
                        <IconedContent icon="exclamation"
                                       orientation="vertical"
                                       align="center"
                                       justify="center"
                                       iconColor="var(--danger-red)">
                            {viewedFile.showPermission.reason.text}
                        </IconedContent>
                    </div>
                {:else}
                    <div class="file-content-container">
                        {#if viewableFileContainsViewForm(viewedFile, FileViewForm.PDF)}
                            <MvPdfFile file="{viewedFile}"/>
                        {/if}

                        {#if viewableFileContainsViewForm(viewedFile, FileViewForm.IMAGE)}
                            <MvImageFile file="{viewedFile}" {imageSwitchAnimationDirection} {pinchZooming}/>
                        {/if}

                        {#if viewableFileContainsViewForm(viewedFile, FileViewForm.PLAIN_TEXT, FileViewForm.RICH_DOCUMENT)}
                            <MvTextFile file="{viewedFile}"/>
                        {/if}

                        {#if viewableFileContainsViewForm(viewedFile, FileViewForm.VIDEO)}
                            <MvVideoFile file="{viewedFile}"/>
                        {/if}

                        {#if viewableFileContainsViewForm(viewedFile, FileViewForm.AUDIO)}
                            <MvAudioFile file="{viewedFile}"/>
                        {/if}
                    </div>
                {/if}
            {:else}
                <div class="unsupported-file-container">
                    <div class="icon-container">
                        <MvDirectoryOrFileIcon directoryOrFile="{viewedFile}"/>
                    </div>

                    <div class="texts-container">
                        <p class="file-name">
                            {viewedFile.text}
                            <span class="file-size">{pipe(viewedFile.size, byteFormatter())}</span>
                        </p>

                        {localize(/* @kp-localization mediaviewer.content.UnsupportedFileType */ 'mediaviewer.content.UnsupportedFileType')}
                    </div>

                    {#if viewedFile.exportPermission.allowed}
                        <KpButton buttonStyle="accent-blue-new" on:click={() => handleFileDownloadClick(viewedFile)}>
                            <IconedContent icon="download">
                                {localize(/* @kp-localization mediaviewer.content.DownloadFileBtnLabel */ 'mediaviewer.content.DownloadFileBtnLabel')}
                            </IconedContent>
                        </KpButton>
                    {/if}
                </div>
            {/if}
        {/if}
    </div>
{/key}

{#if exists(viewedFile) && !presentationMode && viewedFile.showPermission.allowed}
    <div class="floating-controls-container" out:fly={{y: 30}}>
        <MvFloatingControls/>
    </div>
{/if}

<style lang="less">
    @import (reference) "src/features/media-viewer/media-viewer.variables.less";

    .mv-viewed-content {
        flex: 1 1 0;
        width: 100%;
        height: 100%;
        display: flex;
        overflow: hidden;
        flex-direction: column;

        .info-container,
        .unsupported-file-container {
            padding-bottom: 40px;
            max-width: 300px;
            display: flex;
            flex-direction: column;
            align-self: center;
            justify-self: center;
            align-items: center;
            justify-content: center;
            margin-top: auto;
            margin-bottom: auto;
        }

        .file-content-container {
            height: 100%;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .unsupported-file-container {
            gap: @spacing-m;

            .icon-container {
                font-size: 18px;
                color: var(--brand-orange-new);
            }

            .texts-container {
                display: flex;
                align-items: center;
                text-align: center;
                flex-direction: column;
                gap: @spacing-xs;

                .file-name {
                    font-weight: 500;
                    margin: 0;

                    .file-size {
                        font-weight: 400;
                        opacity: 0.75;
                    }
                }
            }

            &.no-view-permission {
                color: var(--danger-red);

                .icon-container {
                    color: var(--danger-red);
                }

                .file-name {
                    color: var(--viewer-label-text-color);
                }
            }
        }
    }

    .floating-controls-container {
        position: absolute;
        bottom: @spacing-ml;
        left: 50%;
        z-index: 10;
        transform: translateX(-50%);
    }
</style>